import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../../components/button";
import { type Colors, useColors } from "../../theme";
import { useVerifyEmail } from "../use-verify-email";
import { Section } from "./section";

interface Props {
  email: string;
  emailVerified: boolean;
}

// Account identity + verification surface. Email always shown; verify-
// affordance only renders when the address is unverified.
export function AccountSection({ email, emailVerified }: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const verify = useVerifyEmail();
  return (
    <Section title="Account">
      <View style={styles.row}>
        <Text style={styles.rowLabel}>Email</Text>
        <View style={styles.emailRowValue}>
          <Text style={styles.rowValue}>{email}</Text>
          {emailVerified ? (
            <Text style={styles.verifiedBadge}>Verified</Text>
          ) : (
            <Text style={styles.unverifiedBadge}>Unverified</Text>
          )}
        </View>
      </View>
      {!emailVerified ? (
        <View style={styles.verifyPanel}>
          {verify.state === "sent" ? (
            <Text style={styles.verifySentText}>
              Sent. Check your inbox for the confirmation link.
            </Text>
          ) : (
            <>
              <Text style={styles.empty}>
                Confirm your email to keep your account active.
              </Text>
              <Button
                variant="secondary"
                fullWidth
                onPress={verify.resend}
                disabled={verify.state === "sending"}
                busy={verify.state === "sending"}
                label={
                  verify.state === "sending"
                    ? "Sending…"
                    : "Send verification email"
                }
                testID="settings-resend-verification"
              />
              {verify.error ? (
                <Text style={styles.error}>{verify.error}</Text>
              ) : null}
            </>
          )}
        </View>
      ) : null}
    </Section>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    row: { gap: space[1] },
    rowLabel: { ...textStyle.caption, color: colors.textMuted },
    rowValue: { ...textStyle.body, color: colors.textPrimary, fontFamily: fontFamily.regular },
    emailRowValue: { flexDirection: "row", alignItems: "center", gap: space[2] },
    verifiedBadge: {
      ...textStyle.caption,
      color: colors.success,
      backgroundColor: colors.surfaceCorrect,
      paddingHorizontal: space[2],
      paddingVertical: 2,
      borderRadius: radius.pill,
    },
    unverifiedBadge: {
      ...textStyle.caption,
      color: colors.caution,
      backgroundColor: colors.surfaceCaution,
      paddingHorizontal: space[2],
      paddingVertical: 2,
      borderRadius: radius.pill,
    },
    verifyPanel: { gap: space[2] },
    verifySentText: { ...textStyle.bodySmall, color: colors.success },
    empty: { ...textStyle.bodySmall, color: colors.textMuted },
    error: { ...textStyle.bodySmall, color: colors.error, textAlign: "center" },
  });
}
